import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Folder, Upload, FolderInput, FileText, FileSpreadsheet, Presentation, FileCode, File, FileType,
} from '../../icons';
import { FileTypeGlyph, type FileGlyphName } from '../../components/FileTypeGlyph';
import { NEW_DOC_GLYPH } from '../../lib/workspace-kind-glyph';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { NEW_DOC_KINDS, type NewDocKind } from '../../lib/workspace-new-doc';

/**
 * `＋ 만들기 또는 업로드` 메뉴 (2026-08-14).
 *
 * 8항목 — 폴더 · 파일 업로드 · 폴더 업로드 · Word · Excel · PowerPoint · Markdown · 텍스트.
 * 앞의 셋은 기존 모달/파일 선택을 그대로 열고, 뒤의 다섯은 빈 문서를 만들어 편집기로 넘긴다.
 *
 * ⚠ **portal 로 body 에 띄운다.** 좌측 상세 메뉴는 세로 스크롤(overflow-y:auto)이라 그 안에
 *   절대배치하면 메뉴가 잘린다. 앵커의 실측 위치(getBoundingClientRect)를 기준으로 붙이고,
 *   화면 아래로 넘치면 위로 올린다. ProfilePopover 가 같은 이유로 같은 방식을 쓴다.
 */

/** 메뉴 폭·여백(px) — CSS `.ws-create-menu` 와 동기 유지. */
const MENU_WIDTH = 248;
const GAP = 6;
const EDGE = 12;

export type CreateAction =
  | { kind: 'folder' }
  | { kind: 'upload-files' }
  | { kind: 'upload-folder' }
  | { kind: 'new-doc'; doc: NewDocKind };

interface Props {
  /** 앵커(만들기 버튼). 메뉴는 이 요소 바로 아래에 붙는다. */
  anchorRef: React.RefObject<HTMLElement>;
  onSelect: (action: CreateAction) => void;
  onClose: () => void;
}

/**
 * 새 문서 항목 (2026-08-18 그림 교체).
 *
 * `glyph` 가 실제로 그려지는 것이고, `icon`·`tone` 은 옛 선 아이콘 폴백이다(그림 없는
 * 항목이 생겨도 빈 칸이 되지 않게 남겨 둔다). 그림 표는 파일 목록·필터 칩과 공용인
 * `lib/workspace-kind-glyph` 에 있다 — 세 곳이 각자 표를 들면 조용히 갈라진다.
 */
const DOC_META: Record<NewDocKind, { icon: typeof FileText; labelKey: WorkspaceI18nKey; tone: string }> = {
  docx: { icon: FileText, labelKey: 'workspace.create.word', tone: 'var(--k-word)' },
  xlsx: { icon: FileSpreadsheet, labelKey: 'workspace.create.excel', tone: 'var(--k-excel)' },
  pptx: { icon: Presentation, labelKey: 'workspace.create.ppt', tone: 'var(--k-ppt)' },
  // 한글 (2026-08-14 2차) — 필터 칩과 같은 그림·같은 색을 쓴다(WorkspaceFilterBar 의 KIND_ICON).
  hwpx: { icon: FileType, labelKey: 'workspace.create.hwp', tone: 'var(--k-hwp)' },
  md: { icon: FileCode, labelKey: 'workspace.create.markdown', tone: 'var(--k-md)' },
  txt: { icon: File, labelKey: 'workspace.create.text', tone: 'var(--k-txt)' },
};

export function WorkspaceCreateMenu({ anchorRef, onSelect, onClose }: Props): JSX.Element | null {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEscapeClose(onClose);

  // 위치는 페인트 **전에** 정한다 — useEffect 로 미루면 (0,0) 에 한 프레임 그려졌다가
  // 제자리로 튀는 것이 보인다.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 360;
    let top = r.bottom + GAP;
    if (top + menuH > window.innerHeight - EDGE) {
      // 아래로 안 들어가면 위로 — 그래도 안 되면 화면 안으로 밀어 넣는다.
      top = Math.max(EDGE, r.top - GAP - menuH);
    }
    const left = Math.min(
      Math.max(EDGE, r.left),
      Math.max(EDGE, window.innerWidth - MENU_WIDTH - EDGE),
    );
    setPos({ top, left });
  }, [anchorRef]);

  // 바깥 클릭 닫기 — 앵커 자신은 제외한다(토글 버튼이 열자마자 닫히지 않도록).
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    }
    // 여는 클릭이 그대로 바깥 클릭으로 잡히지 않도록 한 틱 미룬다.
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('mousedown', onDown); };
  }, [anchorRef, onClose]);

  const item = (
    key: string,
    Icon: typeof Folder,
    labelKey: WorkspaceI18nKey,
    action: CreateAction,
    tone?: string,
    /** 주면 선 아이콘 대신 브랜드 그림을 그린다 — 크기는 16 그대로라 라벨이 안 밀린다. */
    glyph?: FileGlyphName,
  ) => (
    <button
      key={key}
      type="button"
      role="menuitem"
      className="ws-create-item"
      onClick={() => { onSelect(action); onClose(); }}
    >
      {glyph
        ? <FileTypeGlyph name={glyph} size={16} />
        : <Icon size={16} aria-hidden style={tone ? { color: tone } : undefined} />}
      {tWorkspace(labelKey)}
    </button>
  );

  return createPortal(
    <div
      ref={menuRef}
      className="ws-create-menu"
      role="menu"
      aria-label={tWorkspace('workspace.create.aria')}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
    >
      {/* 폴더는 목록에 뜨는 것과 같은 그림. 아래 업로드 둘은 유형이 아니라 **행위**라 선 아이콘. */}
      {item('folder', Folder, 'workspace.create.folder', { kind: 'folder' }, undefined, 'folder')}
      {item('upload-files', Upload, 'workspace.create.upload_files', { kind: 'upload-files' })}
      {item('upload-folder', FolderInput, 'workspace.create.upload_folder', { kind: 'upload-folder' })}

      <div className="ws-create-sep" role="separator" />
      <div className="ws-create-label">{tWorkspace('workspace.create.new_doc')}</div>

      {NEW_DOC_KINDS.map((doc) => {
        const meta = DOC_META[doc];
        return item(doc, meta.icon, meta.labelKey, { kind: 'new-doc', doc }, meta.tone, NEW_DOC_GLYPH[doc]);
      })}
    </div>,
    document.body,
  );
}
